import { Injectable, inject } from '@angular/core';
import { EMPTY, Observable, merge, of } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { VigimareAlertService } from '../../../services/vigimare-alert.service';
import { CyberEvent, alertEvent } from './cyber-event.model';

/**
 * The cyber page's one source of truth: stored alert history followed by live pushes.
 *
 * Detection partners publish onto the `cyberAlerts` Kafka topic; the backend consumer
 * persists each message and fans it out over the existing alert SSE stream, so there is no
 * cyber-specific transport to maintain here.
 */
@Injectable({ providedIn: 'root' })
export class CyberFeedService {
  private alertService = inject(VigimareAlertService);

  live(): Observable<CyberEvent> {
    // Only a `created` push is an alert arriving, and must not be announced again. An `updated`
    // one — a status change, an acknowledgement, an assignment — is an alert the page already has,
    // but it is still news: the server sends an assignment change down this channel, addressed to
    // the user it concerns, so it is kept distinct from stored history rather than folded into it.
    //
    // A `reconnected` marker is neither: it says the stream was down and anything pushed during
    // the gap never arrived. Re-reading history is what closes that gap — without it the alerts
    // raised while the connection was broken stay invisible until the page is reloaded.
    const stream$ = this.alertService.listenForAlertStream().pipe(
      mergeMap((event) =>
        event.type === 'reconnected'
          // Isolated in its own right: a catch-up request that fails must not end the live
          // stream it is nested in, which the outer `isolate` would otherwise do permanently.
          ? isolate(this.history$(), 'alert re-sync')
          : of(alertEvent(event.alert, event.type === 'created' ? 'created' : 'updated')),
      ),
    );

    // Merged rather than concatenated so the SSE connection opens immediately and no push
    // is missed while the history request is still in flight. Each source is isolated: a
    // failing history request must not take the live stream down with it.
    return merge(isolate(this.history$(), 'alert history'), isolate(stream$, 'alert stream'));
  }

  /** Everything already stored, oldest first, replayed as events the page can fold in. */
  private history$(): Observable<CyberEvent> {
    return this.alertService.getAll().pipe(
      map((alerts) => [...alerts].sort(byGeneratedIn)),
      // Spelled out rather than point-free: `map` would hand the array index in as `live`, and
      // every stored alert but the first would claim to have just arrived.
      mergeMap((alerts) => alerts.map((alert) => alertEvent(alert))),
    );
  }
}

function byGeneratedIn(a: { generatedIn: string }, b: { generatedIn: string }): number {
  return a.generatedIn.localeCompare(b.generatedIn);
}

/** Keeps one failing source from tearing down the whole merged feed. */
function isolate(source$: Observable<CyberEvent>, label: string): Observable<CyberEvent> {
  return source$.pipe(
    catchError((err: unknown) => {
      console.warn(`[cyber] ${label} unavailable:`, err);
      return EMPTY;
    }),
  );
}
